// What a user may do, summarised for the Access Management grid. Matches the convention the
// EDU Intelligence and People Management access pages already show customers: role names,
// comma-joined. Two summaries on top of that:
//   - Owner is complete access, so it stands alone whatever else is held.
//   - Holding every core role reads as "All Roles" (the same set the EI picker's chip selects);
//     anything beyond the core set (Safe Space, Head Teacher, ...) is still listed after it.
// Roles are matched by their Identity code, because display names are editable in the role
// catalogue. When the API supplies no codes, the canonical names are mapped back to codes.
import { ASSIGNABLE_ROLE_CODES, SAFE_SPACE_ROLE_CODES, FALLBACK_ROLE_NAMES, roleLabel } from './roleCatalog';

const OWNER_CODE = 'Owner';

export const ALL_ROLES_LABEL = 'All Roles';
export const NO_ROLES_LABEL = '—';

const CORE_ROLE_CODES = ASSIGNABLE_ROLE_CODES.filter((code) => code !== OWNER_CODE);
const CATALOGUE_ORDER = [...CORE_ROLE_CODES, ...SAFE_SPACE_ROLE_CODES];

// Users carry display labels ("Account Owner") as well as Identity's own names ("Owner"), so
// both map back to the code.
const CODE_BY_CANONICAL_NAME = Object.fromEntries([
  ...Object.entries(FALLBACK_ROLE_NAMES).map(([code, name]) => [name, code]),
  ...Object.keys(FALLBACK_ROLE_NAMES).map((code) => [roleLabel(code), code]),
]);

const codeForRoleName = (name) => CODE_BY_CANONICAL_NAME[name] ?? name;

const catalogueRank = (code) => {
  const index = CATALOGUE_ORDER.indexOf(code);
  return index === -1 ? CATALOGUE_ORDER.length : index;
};

const byCatalogueThenName = (a, b) =>
  catalogueRank(a.code) - catalogueRank(b.code) || a.name.localeCompare(b.name);

export function describeAccessLevel({ roles = [], roleCodes = [] } = {}) {
  const held = roles.map((name, index) => {
    const code = roleCodes[index] ?? codeForRoleName(name);
    // A name the catalogue has renamed is shown as Identity sends it; a canonical name gets its label.
    const renamed = name && name !== FALLBACK_ROLE_NAMES[code];
    return { name: renamed ? name : roleLabel(code, { name }), code };
  });
  if (held.length === 0) return NO_ROLES_LABEL;
  const owner = held.find((role) => role.code === OWNER_CODE);
  if (owner) return owner.name;

  const heldCodes = new Set(held.map((role) => role.code));
  const holdsEveryCoreRole = CORE_ROLE_CODES.every((code) => heldCodes.has(code));
  const listed = holdsEveryCoreRole
    ? held.filter((role) => !CORE_ROLE_CODES.includes(role.code))
    : held;
  const names = [...listed].sort(byCatalogueThenName).map((role) => role.name);

  return (holdsEveryCoreRole ? [ALL_ROLES_LABEL, ...names] : names).join(', ');
}
